import React, { FC } from 'react'
import { useTranslation } from 'src/utils/useTranslation'
import * as Styles from './eventCountdown.module.scss'

const SECONDS_IN_DAY = 24 * 60 * 60
const SECONDS_IN_HOUR = 60 * 60
const SECONDS_IN_MINUTE = 60

/**
 * Props for the `LabeledCounter` component.
 */
type LabeledCounterProps = {
  /**
   * The count to display in the counter.
   */
  count: number
  /**
   * The label to display next to the count.
   */
  label: string
}

/**
 * A simple labeled counter component that displays a count and a label next to it.
 *
 * Used internally by the `EventCountdown` component to display the time remaining until an event for each of the time * units (days, hours, minutes, seconds).
 */
const LabeledCounter: FC<LabeledCounterProps> = ({ count, label }) => (
  <div className={Styles.labeledCounter}>
    <span className={Styles.labeledCounterCount}>{count}</span>
    <span className={Styles.labeledCounterLabel}>{label}</span>
  </div>
)

/**
 * Props for the `EventCountdown` component.
 */
export type EventCountdownProps = {
  /**
   * The number of seconds remaining until the event being counted down to.
   */
  secondsUntilEvent: number
}

/**
 * Component that displays the remaining time until an event in days, hours, minutes, and seconds.
 *
 * The microcopy for the labels for each unit of time is localized based on the current locale.
 */
export const EventCountdown: FC<EventCountdownProps> = ({
  secondsUntilEvent,
}) => {
  const { t } = useTranslation()

  // Ensure that the countdown will display zero remaining time if the event has already passed.
  const normalizedSecondsUntilEvent = Math.max(0, secondsUntilEvent)

  const daysRemaining = Math.floor(normalizedSecondsUntilEvent / SECONDS_IN_DAY)
  const hoursRemaining = Math.floor(
    (normalizedSecondsUntilEvent % SECONDS_IN_DAY) / SECONDS_IN_HOUR
  )
  const minutesRemaining = Math.floor(
    (normalizedSecondsUntilEvent % SECONDS_IN_HOUR) / SECONDS_IN_MINUTE
  )
  const secondsRemaining = normalizedSecondsUntilEvent % SECONDS_IN_MINUTE

  return (
    <div className={Styles.eventCountdown}>
      <ul className={Styles.counterGroup}>
        <li className={Styles.counterGroupItem}>
          <LabeledCounter
            count={daysRemaining}
            label={t('components.EventCountdown.days')}
          />
        </li>
        <li className={Styles.counterGroupItem}>
          <LabeledCounter
            count={hoursRemaining}
            label={t('components.EventCountdown.hours')}
          />
        </li>
        <li className={Styles.counterGroupItem}>
          <LabeledCounter
            count={minutesRemaining}
            label={t('components.EventCountdown.minutes')}
          />
        </li>
        <li className={Styles.counterGroupItem}>
          <LabeledCounter
            count={secondsRemaining}
            label={t('components.EventCountdown.seconds')}
          />
        </li>
      </ul>
    </div>
  )
}
